import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Link2,
  Loader2,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { useApp } from "@/context/AppContext";
import { apiFetch } from "@/lib/apiClient";

export const Route = createFileRoute("/lots/ebay-import")({
  component: EbayImportPage,
});

interface BuyerOrder {
  orderId: string;
  seller: string;
  purchaseDate: string;
  itemTitles: string[];
  totalPaid: number;
  shippingCost: number;
  taxAmount: number;
  baseCost: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_WINDOW_DAYS = 30;

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function fromDateInput(s: string, end = false): Date {
  const [y, m, day] = s.split("-").map(Number);
  return new Date(
    y,
    (m ?? 1) - 1,
    day ?? 1,
    end ? 23 : 0,
    end ? 59 : 0,
    end ? 59 : 0,
  );
}
function subtractDays(anchor: Date, days: number): Date {
  return new Date(anchor.getTime() - days * MS_PER_DAY);
}
function formatPurchaseDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

async function fetchPurchases(from: Date, to: Date): Promise<BuyerOrder[]> {
  const params = new URLSearchParams({
    fromDate: from.toISOString(),
    toDate: to.toISOString(),
  });
  const res = await apiFetch(`/api/ebay/purchases?${params.toString()}`);
  if (res.status === 401) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw Object.assign(new Error(body.error ?? "eBay not connected"), {
      notConnected: true,
    });
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as BuyerOrder[];
}

async function fetchAuthUrl(): Promise<string> {
  const res = await apiFetch("/api/ebay/auth/url");
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Failed to get auth URL");
  }
  const data = (await res.json()) as { url: string };
  return data.url;
}

function EbayImportPage() {
  const navigate = useNavigate();
  const { lots } = useApp();

  const [toDate, setToDate] = useState<Date>(() => new Date());
  const [fromDate, setFromDate] = useState<Date>(() =>
    subtractDays(new Date(), DEFAULT_WINDOW_DAYS - 1),
  );

  const [orders, setOrders] = useState<BuyerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notConnected, setNotConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    setError(null);
    setNotConnected(false);
    try {
      setOrders(await fetchPurchases(from, to));
    } catch (e) {
      if (
        e instanceof Error &&
        (e as Error & { notConnected?: boolean }).notConnected
      ) {
        setNotConnected(true);
      } else {
        setError(e instanceof Error ? e.message : "Failed to load purchases");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(fromDate, toDate);
  }, [load, fromDate, toDate]);

  const goBack = () => {
    const newTo = subtractDays(fromDate, 1);
    const newFrom = subtractDays(newTo, DEFAULT_WINDOW_DAYS - 1);
    setFromDate(newFrom);
    setToDate(newTo);
  };
  const goForward = () => {
    const now = new Date();
    const newFrom = new Date(toDate.getTime() + MS_PER_DAY);
    const newTo = new Date(
      Math.min(
        newFrom.getTime() + (DEFAULT_WINDOW_DAYS - 1) * MS_PER_DAY,
        now.getTime(),
      ),
    );
    setFromDate(newFrom);
    setToDate(newTo);
  };
  const isCurrentWindow = (() => {
    const now = new Date();
    return (
      toDate.getFullYear() === now.getFullYear() &&
      toDate.getMonth() === now.getMonth() &&
      toDate.getDate() === now.getDate()
    );
  })();

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const url = await fetchAuthUrl();
      window.open(url, "_blank", "noopener,noreferrer");
      // Auto-retry the purchase fetch once the OAuth round-trip finishes;
      // the user has to come back to this tab to trigger it.
      setTimeout(() => void load(fromDate, toDate), 1500);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to start eBay connection",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleSelect = (order: BuyerOrder) => {
    void navigate({
      to: "/lots/create",
      search: {
        seller: order.seller,
        purchaseLocation: "eBay",
        purchaseCost: order.baseCost.toFixed(2),
        shippingCost: order.shippingCost.toFixed(2),
        taxFees: order.taxAmount.toFixed(2),
        purchaseDate: order.purchaseDate.slice(0, 10),
        ebayOrderId: order.orderId,
        receiptNumber: order.orderId,
        notes: order.itemTitles.join("\n"),
      },
    });
  };

  return (
    <div className="space-y-3">
      <BackButton fallback={{ to: "/lots" }} />
      <h1 className="text-2xl font-bold text-foreground">Import from eBay</h1>

      {/* Date-range nav */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-3">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-muted"
          aria-label="Previous 30 days"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex flex-1 items-center gap-2 text-xs">
          <input
            type="date"
            value={toDateInput(fromDate)}
            max={toDateInput(toDate)}
            onChange={(e) =>
              e.target.value && setFromDate(fromDateInput(e.target.value))
            }
            className="rounded-md border border-border bg-muted px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <span className="text-muted-foreground">→</span>
          <input
            type="date"
            value={toDateInput(toDate)}
            min={toDateInput(fromDate)}
            max={toDateInput(new Date())}
            onChange={(e) =>
              e.target.value && setToDate(fromDateInput(e.target.value, true))
            }
            className="rounded-md border border-border bg-muted px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <button
          type="button"
          onClick={goForward}
          disabled={isCurrentWindow}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-muted disabled:opacity-30"
          aria-label="Next 30 days"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Loading purchases…</p>
        </div>
      ) : notConnected ? (
        <div className="space-y-3 rounded-md border border-border bg-card p-6 text-center">
          <Link2 className="mx-auto h-7 w-7 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">
            eBay Not Connected
          </h2>
          <p className="text-sm text-muted-foreground">
            Connect your eBay account to import your purchase history.
          </p>
          <button
            type="button"
            onClick={() => void handleConnect()}
            disabled={connecting}
            className="inline-flex items-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {connecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            Connect eBay Account
          </button>
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <div className="flex-1">{error}</div>
          <button
            type="button"
            onClick={() => void load(fromDate, toDate)}
            className="rounded-md border border-destructive px-2 py-1 text-xs font-semibold hover:bg-destructive/10"
          >
            Retry
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Inbox className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">
            No purchases found
          </p>
          <p className="text-xs text-muted-foreground">
            No eBay purchases in this date range.
          </p>
          <button
            type="button"
            onClick={goBack}
            className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Check earlier dates
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => {
            const orderKey = order.orderId.trim().toLowerCase();
            const alreadyImported = lots.some(
              (l) =>
                (l.receiptNumber &&
                  l.receiptNumber.trim().toLowerCase() === orderKey) ||
                (l.ebayOrderId &&
                  l.ebayOrderId.trim().toLowerCase() === orderKey),
            );
            return (
              <button
                key={order.orderId}
                type="button"
                onClick={
                  alreadyImported ? undefined : () => handleSelect(order)
                }
                disabled={alreadyImported}
                className={`flex w-full flex-col gap-2 rounded-md border border-border bg-card p-3 text-left transition-opacity ${
                  alreadyImported
                    ? "cursor-not-allowed opacity-60"
                    : "hover:border-secondary/50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {order.seller || "Unknown Seller"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {formatPurchaseDate(order.purchaseDate)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-semibold text-foreground">
                      ${order.totalPaid.toFixed(2)}
                    </span>
                    {alreadyImported ? (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                        Already imported
                      </span>
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {order.itemTitles.length > 0 && (
                  <div className="space-y-0.5 border-t border-border pt-2">
                    {order.itemTitles.slice(0, 2).map((title, i) => (
                      <div
                        key={i}
                        className="truncate text-[11px] text-muted-foreground"
                      >
                        • {title}
                      </div>
                    ))}
                    {order.itemTitles.length > 2 && (
                      <div className="text-[11px] text-muted-foreground">
                        • +{order.itemTitles.length - 2} more
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
                  <CostItem label="Base" value={order.baseCost} />
                  <CostItem label="Shipping" value={order.shippingCost} />
                  <CostItem label="Tax" value={order.taxAmount} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CostItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide">{label}</span>
      <span className="text-xs font-semibold text-foreground">
        ${value.toFixed(2)}
      </span>
    </div>
  );
}
